import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Wallet, BanknoteIcon, Shield, Tag, X, MapPin } from "lucide-react";

const emptyAddress = { full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" };

const Checkout = () => {
  const { items, totals, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState({ ...emptyAddress, full_name: user?.name || "" });
  const [savedAddrs, setSavedAddrs] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [method, setMethod] = useState("cod");
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount }
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/payments/razorpay/config").then((r) => setRzpEnabled(!!r.data.enabled)).catch(() => setRzpEnabled(false));
    api.get("/addresses").then((r) => {
      setSavedAddrs(r.data || []);
      if (r.data?.length) {
        setSelectedAddrId(r.data[0].address_id);
        const { address_id, label, user_id, created_at, ...pure } = r.data[0];
        setAddress(pure);
      }
    }).catch(() => setSavedAddrs([]));
  }, []);

  const chooseSaved = (id) => {
    const a = savedAddrs.find((x) => x.address_id === id);
    if (!a) return;
    setSelectedAddrId(id);
    const { address_id, label, user_id, created_at, ...pure } = a;
    setAddress(pure);
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim().toUpperCase(), subtotal: totals.subtotal });
      setApplied({ code: data.code, discount: data.discount });
      toast.success(`Coupon applied — you save ₹${data.discount.toFixed(0)}`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally { setApplying(false); }
  };

  const removeCoupon = () => { setApplied(null); setCouponInput(""); };

  // Recompute preview after discount
  const discount = applied?.discount || 0;
  const discountedSubtotal = Math.max(0, totals.subtotal - discount);
  const delivery = discountedSubtotal >= 499 || discountedSubtotal === 0 ? 0 : 40;
  const tax = +(discountedSubtotal * 0.05).toFixed(2);
  const total = +(discountedSubtotal + delivery + tax).toFixed(2);

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-heading text-3xl text-[#2C3E2D]">Your basket is empty</h1>
        <Button onClick={() => navigate("/shop")} className="btn-primary rounded-full mt-6 px-6">Browse products</Button>
      </div>
    );
  }

  const placeOrder = async () => {
    for (const k of ["full_name", "phone", "line1", "city", "state", "pincode"]) {
      if (!address[k]) { toast.error("Please complete the delivery address"); return; }
    }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price,
          quantity: i.quantity, image_url: i.image_url, unit: i.unit,
        })),
        address,
        payment_method: method,
        notes,
        coupon_code: applied?.code || null,
      };
      const { data: order } = await api.post("/orders", payload);

      if (method === "razorpay") {
        try {
          const { data } = await api.post(`/payments/razorpay/create/${order.order_id}`);
          await loadRazorpayScript();
          const rzp = new window.Razorpay({
            key: data.key_id, amount: data.amount, currency: data.currency,
            name: "Gramo Systems", description: `Order ${order.order_id}`,
            order_id: data.razorpay_order_id,
            prefill: { name: address.full_name, email: user?.email || "", contact: address.phone },
            theme: { color: "#E07A5F" },
            handler: async (resp) => {
              try {
                await api.post("/payments/razorpay/verify", {
                  order_id: order.order_id,
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                });
                clear();
                navigate(`/order-success/${order.order_id}`);
              } catch { toast.error("Payment verification failed"); }
            },
          });
          rzp.open();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Razorpay unavailable. Try Cash on Delivery.");
        }
      } else {
        clear();
        navigate(`/order-success/${order.order_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to place order");
    } finally { setPlacing(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 md:px-10 py-12" data-testid="checkout-page">
      <div className="tick-label mb-2">Checkout</div>
      <h1 className="font-heading text-4xl text-[#2C3E2D] font-medium">Almost there</h1>
      <p className="text-sm text-[#6B7B6C] mt-2">Confirm your delivery and payment details.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2 space-y-8">
          {savedAddrs.length > 0 && (
            <section className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="section-saved-addr">
              <h2 className="font-heading text-xl text-[#2C3E2D] mb-4 flex items-center gap-2"><MapPin className="h-4 w-4" /> Saved addresses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {savedAddrs.map((a) => (
                  <label key={a.address_id} className={`cursor-pointer rounded-xl border p-4 transition-colors ${selectedAddrId === a.address_id ? "border-[#81B29A] bg-[#F0EFEA]" : "border-[#E5E5E0]"}`} data-testid={`saved-addr-${a.address_id}`}>
                    <div className="flex items-start gap-2">
                      <input type="radio" name="saved-addr" checked={selectedAddrId === a.address_id} onChange={() => chooseSaved(a.address_id)} />
                      <div className="flex-1 text-sm">
                        <div className="text-xs uppercase tracking-widest text-[#81B29A] font-semibold">{a.label}</div>
                        <div className="text-[#2C3E2D] font-medium">{a.full_name} · {a.phone}</div>
                        <div className="text-[#6B7B6C]">{a.line1}, {a.city} — {a.pincode}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="section-address">
            <h2 className="font-heading text-xl text-[#2C3E2D] mb-4">Delivery address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" value={address.full_name} onChange={(v) => setAddress({ ...address, full_name: v })} testid="addr-name" />
              <Field label="Phone" value={address.phone} onChange={(v) => setAddress({ ...address, phone: v })} testid="addr-phone" />
              <Field label="Address line 1" value={address.line1} onChange={(v) => setAddress({ ...address, line1: v })} testid="addr-line1" full />
              <Field label="Address line 2 (optional)" value={address.line2} onChange={(v) => setAddress({ ...address, line2: v })} testid="addr-line2" full />
              <Field label="City" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} testid="addr-city" />
              <Field label="State" value={address.state} onChange={(v) => setAddress({ ...address, state: v })} testid="addr-state" />
              <Field label="Pincode" value={address.pincode} onChange={(v) => setAddress({ ...address, pincode: v })} testid="addr-pincode" />
            </div>
          </section>

          <section className="rounded-2xl border border-[#E5E5E0] bg-white p-6" data-testid="section-payment">
            <h2 className="font-heading text-xl text-[#2C3E2D] mb-4">Payment method</h2>
            <RadioGroup value={method} onValueChange={setMethod} className="space-y-3">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${method === "cod" ? "border-[#81B29A] bg-[#F0EFEA]" : "border-[#E5E5E0]"}`} data-testid="pay-cod-option">
                <RadioGroupItem value="cod" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium text-[#2C3E2D]"><BanknoteIcon className="h-4 w-4" /> Cash on Delivery</div>
                  <div className="text-sm text-[#6B7B6C]">Pay in cash when your order arrives.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${!rzpEnabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${method === "razorpay" ? "border-[#81B29A] bg-[#F0EFEA]" : "border-[#E5E5E0]"}`} data-testid="pay-razorpay-option">
                <RadioGroupItem value="razorpay" className="mt-1" disabled={!rzpEnabled} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium text-[#2C3E2D]"><Wallet className="h-4 w-4" /> Razorpay (Card / UPI / Netbanking)</div>
                  <div className="text-sm text-[#6B7B6C]">Instant payment via UPI, card or netbanking. {!rzpEnabled && <span className="text-[#E07A5F]">(Setup pending — keys required)</span>}</div>
                </div>
              </label>
            </RadioGroup>

            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">Order notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Leave at the reception, ring bell twice…" className="mt-2 h-11 rounded-xl bg-white" data-testid="order-notes" />
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 sticky top-24 space-y-4" data-testid="order-summary">
            <h2 className="font-heading text-xl text-[#2C3E2D]">Order summary</h2>

            {/* Coupon */}
            <div className="rounded-xl bg-[#F9F8F6] p-3">
              {applied ? (
                <div className="flex items-center justify-between" data-testid="applied-coupon">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-[#81B29A]" />
                    <div>
                      <div className="text-sm font-medium text-[#2C3E2D]">{applied.code}</div>
                      <div className="text-xs text-[#81B29A]">−₹{applied.discount.toFixed(2)} applied</div>
                    </div>
                  </div>
                  <button onClick={removeCoupon} className="text-xs text-[#6B7B6C] hover:text-red-500 inline-flex items-center gap-1" data-testid="remove-coupon-btn">
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Have a promo code?"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                    className="h-10 rounded-lg bg-white text-sm"
                    data-testid="coupon-input"
                  />
                  <Button onClick={applyCoupon} disabled={applying} className="btn-outline-dark rounded-lg h-10 px-4 text-sm" data-testid="apply-coupon-btn">
                    {applying ? "…" : "Apply"}
                  </Button>
                </div>
              )}
              <div className="text-[11px] text-[#6B7B6C] mt-2">Try <b>WELCOME10</b>, <b>FRESH50</b>, <b>MEAL15</b></div>
            </div>

            <div className="max-h-52 overflow-y-auto space-y-3 pr-1 no-scrollbar">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3 items-center text-sm">
                  <img src={i.image_url} className="h-11 w-11 rounded-lg object-cover" alt={i.name} />
                  <div className="flex-1 truncate">
                    <div className="font-medium text-[#2C3E2D] truncate">{i.name}</div>
                    <div className="text-xs text-[#6B7B6C]">×{i.quantity} · {i.unit}</div>
                  </div>
                  <div className="text-sm font-medium text-[#2C3E2D]">₹{(i.price * i.quantity).toFixed(0)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E5E5E0] pt-4 space-y-2 text-sm">
              <Row label="Subtotal" value={`₹${totals.subtotal.toFixed(2)}`} testid="sum-subtotal" />
              {discount > 0 && <Row label={`Discount (${applied.code})`} value={`−₹${discount.toFixed(2)}`} testid="sum-discount" positive />}
              <Row label="Delivery" value={delivery === 0 ? "Free" : `₹${delivery.toFixed(2)}`} testid="sum-delivery" />
              <Row label="Tax (5%)" value={`₹${tax.toFixed(2)}`} testid="sum-tax" />
              <div className="flex justify-between pt-2 border-t border-[#E5E5E0]">
                <span className="font-heading text-lg text-[#2C3E2D]">Total</span>
                <span className="font-heading text-lg font-semibold text-[#2C3E2D]" data-testid="sum-total">₹{total.toFixed(2)}</span>
              </div>
            </div>
            <Button onClick={placeOrder} disabled={placing} className="btn-primary rounded-full w-full h-11" data-testid="place-order-btn">
              {placing ? "Placing order…" : method === "razorpay" ? "Pay with Razorpay" : "Place Order"}
            </Button>
            <div className="flex items-center gap-2 text-xs text-[#6B7B6C] justify-center">
              <Shield className="h-3.5 w-3.5" /> Secure checkout · SSL encrypted
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testid, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-xs uppercase tracking-widest text-[#6B7B6C]">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 h-11 rounded-xl bg-white" data-testid={testid} />
  </div>
);

const Row = ({ label, value, testid, positive }) => (
  <div className="flex justify-between items-center text-[#6B7B6C]">
    <span>{label}</span>
    <span className={`font-medium ${positive ? "text-[#81B29A]" : "text-[#2C3E2D]"}`} data-testid={testid}>{value}</span>
  </div>
);

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default Checkout;
